// src/components/pages/Auth/AuthPage.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

import { getPrimaryColor } from "../../GlobalFunction/Theme";
import { LANG_COOKIE, getLangFromCookie } from "../../../Utils/LangCookie";
import { wpGraphql } from "../../../api/wpGraphql";
import { useAuth } from "./AuthContext";

// ✅ NEW: use cached auth to redirect if already logged in
import { wpGetCachedUser } from "../../../api/wpAuth";

import AuthLoginForm from "./AuthLoginForm";
import AuthRegisterForm from "./AuthRegisterForm";

/* =========================================================
   WP GraphQL mutations/queries
========================================================= */

const REGISTER_MUTATION = `
mutation SehoRegisterCustomer($input: SehoRegisterCustomerInput!) {
  sehoRegisterCustomer(input: $input) {
    ok
    message
    authToken
    refreshToken
    user { id username }
  }
}
`;

/* -------------------- Lang helpers -------------------- */
const normalizeLang = (code) => (code === "EN" || code === "JP" ? code : "ZH");

const getCurrentLangCode = () => {
  const fromCookie = getLangFromCookie();
  const fromLS =
    typeof window !== "undefined"
      ? window.localStorage.getItem(LANG_COOKIE)
      : null;
  return normalizeLang(fromCookie || fromLS || "ZH");
};

/* =========================================================
   TEXT
========================================================= */

const AUTH_TEXT = {
  ZH: {
    loginTitle: "登入",
    signupTitle: "註冊",
    tabLogin: "登入",
    tabSignup: "註冊",

    loginMethodPhone: "手機號碼",
    loginMethodUsername: "用戶名稱",
    usernameLabel: "用戶名稱",
    passwordLabel: "密碼",
    confirmPasswordLabel: "確認密碼",
    keepLogin: "保持登入",
    signupLastName: "姓",
    signupFirstName: "名",
    signupEmail: "電子郵件",

    loginSwitchToUsername: "用戶名稱",
    loginSwitchToPhone: "手機號碼",
    forgotUsername: "忘記用戶名稱？",
    forgotPassword: "忘記密碼？",

    otpLabel: "Email 驗證碼",
    otpSend: "寄送驗證碼",
    otpResend: "重新寄送",
    otpHint: "請輸入寄到信箱的 6 位數驗證碼。若沒收到，請檢查垃圾郵件。",
    otpNeedEmail: "請先輸入電子郵件。",
    otpSent: "驗證碼已寄出。",
    otpVerified: "驗證成功。",
    otpInvalid: "驗證碼無效或已過期，請重新寄送。",
    otpExpired: "驗證碼已過期，請重新寄送。",
    otpRateLimited: "寄送太頻繁，請稍後再試。",
    otpRequired: "需要 Email 驗證碼，請先寄送並輸入驗證碼。",
    otpTimerPrefix: "有效時間：",

    loginPhoneFormatError: "手機號碼格式需為台灣 09XXXXXXXX",
    loginUsernameRequired: "請輸入用戶名稱",
    signupUsernameRequired: "請輸入用戶名稱",
    signupPhoneFormatError: "手機號碼格式需為台灣 09XXXXXXXX",
    signupPwdMin: "密碼至少 8 個字元，且不得超過 20 個字元。",
    signupPwdMismatch: "兩次輸入的密碼不一致",
    phoneFormatShort: "格式需為 09XXXXXXXX",
    requiredField: "此欄位為必填。",

    loginFailed: "登入失敗",
    signupFailed: "註冊失敗",

    showPassword: "顯示密碼",
    hidePassword: "隱藏密碼",

    loginButton: "登入",
    signupButton: "註冊",
    success: "成功！",

    registerDoneTitle: "註冊完成",
    registerDoneBody: "您的註冊已完成，現在可以使用帳號登入。",
    registerDoneGoLogin: "前往登入",

    pwdRule1: "密碼長度需為 8–20 碼。",
    pwdRule3: "不可以與用戶名稱完全相同。",
    pwdRule4: "避免使用易猜測或公開資訊（例如 1234、電話、姓名）。",
    pwdRule5: "需同時包含數字、英文字母、特殊符號，並混合大小寫。",

    invalidCredentials: "用戶名稱或密碼無效",

    sessionExpired: "登入已失效，請重新登入。",
    usernameTaken: "此用戶名稱已被使用。",
    phoneTaken: "此手機號碼已被註冊。",
    emailTaken: "此電子郵件已被註冊。",
  },
  EN: {
    loginTitle: "Log in",
    signupTitle: "Sign up",
    tabLogin: "Log in",
    tabSignup: "Sign up",

    loginMethodPhone: "Phone number",
    loginMethodUsername: "Username",
    usernameLabel: "Username",
    passwordLabel: "Password",
    confirmPasswordLabel: "Confirm password",
    keepLogin: "Keep me signed in",
    signupLastName: "Last name",
    signupFirstName: "First name",
    signupEmail: "Email",

    loginSwitchToUsername: "Username",
    loginSwitchToPhone: "Phone number",
    forgotUsername: "Forgot username?",
    forgotPassword: "Forgot password?",

    otpLabel: "Email OTP",
    otpSend: "Send OTP",
    otpResend: "Resend",
    otpHint:
      "Enter the 6-digit code sent to your email. Check spam/junk if you don't see it.",
    otpNeedEmail: "Please enter your email first.",
    otpSent: "OTP has been sent.",
    otpVerified: "Verified.",
    otpInvalid: "Invalid or expired code. Please resend.",
    otpExpired: "Code expired. Please resend.",
    otpRateLimited: "Too many requests. Please try again later.",
    otpRequired: "Email OTP is required. Please send and enter the code.",
    otpTimerPrefix: "Valid for:",

    loginPhoneFormatError:
      "Phone number must be a Taiwan number in 09XXXXXXXX format.",
    loginUsernameRequired: "Please enter your username.",
    signupUsernameRequired: "Please enter a username.",
    signupPhoneFormatError:
      "Phone number must be a Taiwan number in 09XXXXXXXX format.",
    signupPwdMin: "Password must be 8–20 characters long.",
    signupPwdMismatch: "The two password entries do not match.",
    phoneFormatShort: "Format must be 09XXXXXXXX.",
    requiredField: "This field is required.",

    loginFailed: "Login failed.",
    signupFailed: "Sign up failed.",

    showPassword: "Show password",
    hidePassword: "Hide password",

    loginButton: "Log in",
    signupButton: "Sign up",
    success: "Success!",

    registerDoneTitle: "Registration complete",
    registerDoneBody: "Your registration is complete. You can now log in.",
    registerDoneGoLogin: "Go to login",

    pwdRule1: "Password length must be between 8–20 characters.",
    pwdRule3: "Password cannot be exactly the same as the username.",
    pwdRule4:
      "Avoid easy-to-guess or public info (e.g. 1234, phone number, name).",
    pwdRule5:
      "Must include numbers, English letters, special symbols / other scripts, and both upper & lower case.",

    invalidCredentials: "Invalid username or password.",

    sessionExpired: "Session expired. Please log in again.",
    usernameTaken: "This username is already taken.",
    phoneTaken: "This phone number is already registered.",
    emailTaken: "This email is already registered.",
  },
  JP: {
    loginTitle: "ログイン",
    signupTitle: "新規登録",
    tabLogin: "ログイン",
    tabSignup: "新規登録",

    loginMethodPhone: "携帯電話番号",
    loginMethodUsername: "ユーザー名",
    usernameLabel: "ユーザー名",
    passwordLabel: "パスワード",
    confirmPasswordLabel: "パスワード(再確認)",
    keepLogin: "次回から自動ログイン",
    signupLastName: "姓",
    signupFirstName: "名",
    signupEmail: "メールアドレス",

    loginSwitchToUsername: "ユーザー名",
    loginSwitchToPhone: "携帯電話番号",
    forgotUsername: "ユーザー名をお忘れですか？",
    forgotPassword: "パスワードをお忘れですか？",

    otpLabel: "メール認証コード",
    otpSend: "送信",
    otpResend: "再送",
    otpHint:
      "メールに届いた 6 桁のコードを入力してください。届かない場合は迷惑メールをご確認ください。",
    otpNeedEmail: "先にメールアドレスを入力してください。",
    otpSent: "認証コードを送信しました。",
    otpVerified: "認証しました。",
    otpInvalid: "コードが無効、または期限切れです。再送してください。",
    otpExpired: "コードの有効期限が切れました。再送してください。",
    otpRateLimited: "送信が多すぎます。しばらくしてから再試行してください。",
    otpRequired: "メール認証が必要です。送信してコードを入力してください。",
    otpTimerPrefix: "有効時間：",

    loginPhoneFormatError:
      "電話番号は台湾の 09XXXXXXXX 形式で入力してください。",
    loginUsernameRequired: "ユーザー名を入力してください。",
    signupUsernameRequired: "ユーザー名を入力してください。",
    signupPhoneFormatError:
      "電話番号は台湾の 09XXXXXXXX 形式で入力してください。",
    signupPwdMin: "パスワードは 8〜20 文字で入力してください。",
    signupPwdMismatch: "2 回入力したパスワードが一致しません。",
    phoneFormatShort: "形式は 09XXXXXXXX で入力してください。",
    requiredField: "空欄に記入してください",

    loginFailed: "ログインに失敗しました。",
    signupFailed: "新規登録に失敗しました。",

    showPassword: "パスワードを表示",
    hidePassword: "パスワードを非表示",

    loginButton: "ログインする",
    signupButton: "新規登録",
    success: "成功しました！",

    registerDoneTitle: "登録完了",
    registerDoneBody: "登録が完了しました。ログインしてご利用ください。",
    registerDoneGoLogin: "ログインへ",

    pwdRule1: "パスワードは 8〜20 文字で入力してください。",
    pwdRule3: "ユーザー名とまったく同じパスワードは使用できません。",
    pwdRule4: "1234・電話番号・氏名など推測されやすい情報は避けてください。",
    pwdRule5:
      "数字・英字・記号・その他の文字を使い、大文字と小文字を混在させてください。",

    invalidCredentials: "ユーザー名またはパスワードが無効です",

    sessionExpired: "ログイン情報が無効です。再ログインしてください。",
    usernameTaken: "このユーザー名は既に使用されています。",
    phoneTaken: "この電話番号は既に登録されています。",
    emailTaken: "このメールアドレスは既に登録されています。",
  },
};

const tForLang = (lang, key) => {
  const l = normalizeLang(lang);
  return AUTH_TEXT[l]?.[key] ?? AUTH_TEXT.ZH?.[key] ?? key;
};

/* =========================================================
   Helper: query param for mode
========================================================= */

function getModeFromLocation(location) {
  const qs = new URLSearchParams(location.search || "");
  const mode = (qs.get("mode") || "login").toLowerCase();
  return mode === "register" ? "register" : "login";
}

function setModeToLocation(navigate, location, mode) {
  const qs = new URLSearchParams(location.search || "");
  qs.set("mode", mode);
  navigate(
    { pathname: location.pathname, search: `?${qs.toString()}` },
    { replace: true }
  );
}

/* =========================================================
   Component
========================================================= */

export default function AuthPage() {
  const rail = getPrimaryColor("b2b");
  const navigate = useNavigate();
  const location = useLocation();

  const { signIn, requestEmailOtp, verifyEmailOtp } = useAuth();

  const redirectTo =
    (location.state && location.state.redirectTo) || "/profile";

  const [lang, setLang] = useState(getCurrentLangCode());
  const t = (key) => tForLang(lang, key);

  const [mode, setMode] = useState(() => getModeFromLocation(location)); // login | register
  const [loading, setLoading] = useState(false);

  const [notif, setNotif] = useState(false);
  const [registerDoneOpen, setRegisterDoneOpen] = useState(false);

  // ✅ NEW: redirect away from /auth if already logged in
  useEffect(() => {
    const cached = wpGetCachedUser?.();
    const hasUser = !!cached?.id;
    const hasToken =
      typeof window !== "undefined" ? !!localStorage.getItem("wp_jwt") : false;

    if (hasUser && hasToken) {
      navigate("/profile", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // sync when user navigates (back/forward)
    setMode(getModeFromLocation(location));
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const syncLang = () => {
      const code = getCurrentLangCode();
      setLang((prev) => (prev === code ? prev : code));
    };

    window.addEventListener("app:languageChanged", syncLang);
    window.addEventListener("storage", syncLang);

    const timer = setInterval(syncLang, 300);
    syncLang();

    return () => {
      window.removeEventListener("app:languageChanged", syncLang);
      window.removeEventListener("storage", syncLang);
      clearInterval(timer);
    };
  }, []);

  async function handleLogin({ identifier, password, keepLogin }) {
    if (loading) return;
    setLoading(true);

    try {
      await signIn({
        identifier,
        password,
        keepLogin,
      });

      setNotif(true);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(inputFromForm) {
    if (loading) return;
    setLoading(true);

    try {
      const input = { ...inputFromForm };
      Object.keys(input).forEach((k) => {
        if (input[k] === undefined) delete input[k];
        if (typeof input[k] === "string" && input[k].trim() === "")
          delete input[k];
      });

      const reg = await wpGraphql(
        REGISTER_MUTATION,
        { input },
        {
          token: "",
          requestTag: "register",
          operationName: "SehoRegisterCustomer",
          allowRegisterSideEffect: true,
          debug: false,
        }
      );

      const ok =
        reg?.__seho_registerSideEffectLikely ||
        reg?.sehoRegisterCustomer?.ok === true;

      if (!ok) {
        const msg = reg?.sehoRegisterCustomer?.message || t("signupFailed");
        throw new Error(msg);
      }

      setRegisterDoneOpen(true);

      setModeToLocation(navigate, location, "login");
      setMode("login");
    } finally {
      setLoading(false);
    }
  }

  const showLogin = mode === "login";

  return (
    <Box sx={{ display: "grid", placeItems: "center", py: { xs: 4, md: 8 } }}>
      <Backdrop
        open={!!loading}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <CircularProgress />
      </Backdrop>

      <Paper
        sx={{
          width: 480,
          maxWidth: "92vw",
          p: { xs: 2.5, md: 3.5 },
          borderTop: () => `6px solid ${rail}`,
        }}
        elevation={3}
      >
        {showLogin ? (
          <AuthLoginForm
            rail={rail}
            t={t}
            lang={lang}
            loading={loading}
            onSubmitLogin={handleLogin}
            onGoRegister={() => {
              setModeToLocation(navigate, location, "register");
              setMode("register");
            }}
          />
        ) : (
          <AuthRegisterForm
            rail={rail}
            lang={lang}
            t={t}
            loading={loading}
            onSubmitRegister={handleRegister}
            onGoLogin={() => {
              setModeToLocation(navigate, location, "login");
              setMode("login");
            }}
            requestEmailOtp={requestEmailOtp}
            verifyEmailOtp={verifyEmailOtp}
          />
        )}
      </Paper>

      <Snackbar
        open={notif}
        autoHideDuration={2000}
        onClose={() => setNotif(false)}
      >
        <Alert
          onClose={() => setNotif(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {t("success")}
        </Alert>
      </Snackbar>

      <Dialog
        open={registerDoneOpen}
        onClose={() => {
          setRegisterDoneOpen(false);
          setModeToLocation(navigate, location, "login");
          setMode("login");
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {lang === "ZH"
            ? "註冊完成"
            : lang === "JP"
            ? "登録完了"
            : "Registration complete"}
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
            {lang === "ZH"
              ? "您的帳號已建立成功，現在可以使用手機號碼或用戶名稱登入。"
              : lang === "JP"
              ? "アカウント作成が完了しました。電話番号またはユーザー名でログインできます。"
              : "Your account has been created. You can now log in using your phone number or username."}
          </Typography>

          <Box sx={{ mt: 1.25 }}>
            <Typography
              sx={{ fontSize: 13, color: "text.secondary", fontWeight: 100 }}
            >
              {lang === "ZH"
                ? "登入後可到會員專區更新生日與性別等資料。"
                : lang === "JP"
                ? "ログイン後、会員ページで生年月日・性別などを更新できます。"
                : "After logging in, you can update your profile (DOB, gender, etc.) in the member page."}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              setRegisterDoneOpen(false);
              setModeToLocation(navigate, location, "login");
              setMode("login");
            }}
            sx={{ bgcolor: rail }}
          >
            {lang === "ZH"
              ? "前往登入"
              : lang === "JP"
              ? "ログインへ"
              : "Go to login"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
